import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '../classes/BaseComponent';
import { Global } from '../classes/Global';
import { StorageHelper } from '../helpers/StorageHelper';
import { InternationalizationHelper } from '../helpers/InternationalizationHelper';
import { CountryPollService } from '../country-poll.service';

interface CountryPollCountry {
  code: string;
  name: string;
  votes: number;
}

@Component({
  selector: 'app-country-poll',
  templateUrl: './country-poll.component.html',
  styleUrls: ['./country-poll.component.scss'],
  providers: [ CountryPollService ]
})
export class CountryPollComponent extends BaseComponent implements OnInit {

  countries: CountryPollCountry[] = [];
  filteredCountries: CountryPollCountry[] = [];
  topCountries: CountryPollCountry[] = [];
  selectedCountry: CountryPollCountry = null;
  searchTerm = '';

  countriesLoading = false;
  resultsLoading = false;
  voteLoading = false;

  countriesError = '';
  resultsError = '';
  voteError = '';
  successMessage = '';

  private readonly clientIdKey = 'cc_country_poll_client_id';

  constructor(
    private countryPollService: CountryPollService,
    private route: ActivatedRoute
  ) {
    super();
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      const countryKey = params['country'];
      if (countryKey && Global.countries[countryKey]) {
        StorageHelper.getInstance().save(StorageHelper.keys.country, countryKey);
      }
    });

    this.loadCountries();
    this.loadResults();
  }

  loadCountries() {
    this.countriesLoading = true;
    this.countriesError = '';

    this.countryPollService.getCountries().subscribe(res => {
      this.countries = this.normalizeCountriesResponse(res);
      this.filterCountries();
      this.countriesLoading = false;
      if (this.countries.length === 0) {
        this.countriesError = this.getString('countryPollNoCountriesAvailable');
      }
    }, err => {
      this.countries = [];
      this.filteredCountries = [];
      this.countriesLoading = false;
      this.countriesError = this.getString('countryPollCouldNotLoadCountries');
    });
  }

  loadResults() {
    this.resultsLoading = true;
    this.resultsError = '';

    this.countryPollService.getResults().subscribe(res => {
      this.topCountries = this.normalizeCountriesResponse(res, false).slice(0, 5);
      this.resultsLoading = false;
    }, err => {
      this.topCountries = [];
      this.resultsLoading = false;
      this.resultsError = this.getString('countryPollCouldNotLoadTopCountries');
    });
  }

  onSearchChanged() {
    this.successMessage = '';
    this.voteError = '';
    this.filterCountries();
  }

  filterCountries() {
    const query = (this.searchTerm || '').toLowerCase().trim();
    let matches = this.countries;

    if (query) {
      matches = this.countries.filter(country => {
        return country.name.toLowerCase().indexOf(query) >= 0 ||
          this.getCountryName(country).toLowerCase().indexOf(query) >= 0 ||
          country.code.toLowerCase().indexOf(query) >= 0;
      });
    }

    this.filteredCountries = matches;
  }

  selectCountry(country: CountryPollCountry) {
    this.selectedCountry = country;
    this.searchTerm = this.getCountryName(country);
    this.successMessage = '';
    this.voteError = '';
    this.filterCountries();
  }

  vote() {
    if (!this.selectedCountry || this.voteLoading) {
      return;
    }

    this.voteLoading = true;
    this.voteError = '';
    this.successMessage = '';

    this.countryPollService.vote(this.selectedCountry.code, this.getClientId()).subscribe(res => {
      this.voteLoading = false;
      if (res && res['status'] && res['status'] !== 'success') {
        this.voteError = res['message'] || this.getString('countryPollVoteCouldNotBeSaved');
        return;
      }
      this.successMessage = this.getString('countryPollThankYou', { country: this.getCountryName(this.selectedCountry) });
      this.loadResults();
    }, err => {
      this.voteLoading = false;
      if (err && err.status === 429 && err.error && err.error.message) {
        this.voteError = err.error.message;
        if (err.error.results) {
          this.topCountries = this.normalizeCountriesResponse({ results: err.error.results }, false).slice(0, 5);
        }
        return;
      }
      if (err && err.error && err.error.message) {
        this.voteError = err.error.message;
        return;
      }
      this.voteError = this.getString('countryPollVoteCouldNotBeSavedRetry');
    });
  }

  getFlagUrl(country: CountryPollCountry): string {
    if (!country || !country.code) {
      return '';
    }
    let code = country.code.toLowerCase();
    if (code === 'uk') {
      code = 'gb';
    }
    return 'https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/1x1/' + code + '.svg';
  }

  getCountryName(country: CountryPollCountry): string {
    if (!country) {
      return '';
    }

    const language = InternationalizationHelper.getTwoCharactersLanguage();
    try {
      const intlRef = (window as any).Intl;
      if (intlRef && intlRef.DisplayNames && country.code) {
        const displayNames = new intlRef.DisplayNames([language], { type: 'region' });
        const localizedName = displayNames.of(country.code.toUpperCase());
        if (localizedName) {
          return localizedName;
        }
      }
    } catch (e) {}

    return country.name;
  }

  getVoteCountText(country: CountryPollCountry): string {
    const votes = country && country.votes ? country.votes : 0;
    const key = votes === 1 ? 'countryPollVoteSingular' : 'countryPollVotePlural';
    return votes + ' ' + this.getString(key);
  }

  getVotePercent(country: CountryPollCountry): number {
    const maxVotes = this.getMaxVotes();
    if (!country || !country.votes || maxVotes <= 0) {
      return 0;
    }
    return Math.max(4, Math.round((country.votes / maxVotes) * 100));
  }

  trackByCountryCode(index: number, country: CountryPollCountry): string {
    return country ? country.code : String(index);
  }

  private getMaxVotes(): number {
    if (!this.topCountries || this.topCountries.length === 0) {
      return 0;
    }
    return this.topCountries[0].votes || 0;
  }

  private getClientId(): string {
    let clientId = '';
    try {
      clientId = localStorage.getItem(this.clientIdKey);
      if (!clientId) {
        clientId = 'ccp-' + new Date().getTime() + '-' + Math.floor(Math.random() * 1000000000);
        localStorage.setItem(this.clientIdKey, clientId);
      }
    } catch (e) {
      clientId = 'ccp-session-' + new Date().getTime() + '-' + Math.floor(Math.random() * 1000000000);
    }
    return clientId;
  }

  private normalizeCountriesResponse(res: any, sortByName: boolean = true): CountryPollCountry[] {
    let rawCountries: any[] = [];

    if (res instanceof Array) {
      rawCountries = res;
    } else if (res && res['countries'] instanceof Array) {
      rawCountries = res['countries'];
    } else if (res && res['results'] instanceof Array) {
      rawCountries = res['results'];
    } else if (res && res['top'] instanceof Array) {
      rawCountries = res['top'];
    } else if (res && res['topCountries'] instanceof Array) {
      rawCountries = res['topCountries'];
    }

    const countries = rawCountries.map(raw => this.normalizeCountry(raw))
      .filter(country => !!country.code);

    if (sortByName) {
      return countries.sort((a, b) => a.name.localeCompare(b.name));
    }

    return countries.sort((a, b) => {
      if (b.votes !== a.votes) {
        return b.votes - a.votes;
      }
      return a.name.localeCompare(b.name);
    });
  }

  private normalizeCountry(raw: any): CountryPollCountry {
    const code = this.readString(raw, ['countryCode', 'code', 'key', 'id']).toLowerCase();
    let name = this.readString(raw, ['displayName', 'name', 'countryName', 'title', 'label']);
    if (!name) {
      name = code.toUpperCase();
    }

    return {
      code: code,
      name: name,
      votes: this.readNumber(raw, ['votes', 'voteCount', 'count', 'total'])
    };
  }

  private readString(raw: any, keys: string[]): string {
    if (!raw) {
      return '';
    }
    for (let i = 0; i < keys.length; i++) {
      const value = raw[keys[i]];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  }

  private readNumber(raw: any, keys: string[]): number {
    const value = this.readString(raw, keys);
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

}
